import React, { useCallback, useMemo, useState } from 'react'
import { useCurrentCloudProvider } from '@labrav/common'
import { MainTabsV2 } from '@labrav/react-components'
import { useStyles } from '../MarketplaceTemplateTableContainer.styles'
import { MarketplaceFieldMapping } from '../../../../../modules/marketplaceMappingManager/types'
import { MarketplaceRecipientSourceTab } from './MarketplaceRecipientSourceTab/MarketplaceRecipientSourceTab'
import { MarketplaceInstallmentsTab } from './MarketplaceInstallmentsTab/MarketplaceInstallmentsTab'
import { MarketplaceTabFieldConfiguration } from './MarketplaceTabFieldConfiguration/MarketplaceTabFieldConfiguration'
import { MarketplaceTemplateDimensions } from './MarketplaceTemplateDimensions/MarketplaceTemplateDimensions'
import { MarketplaceTemplateMetadataTab } from './MarketplaceTemplateMetadataTab/MarketplaceTemplateMetadataTab'
import { CloudType } from '../../../../../types/external'

export interface MarketplaceTemplateTabRenderProps {
  isViewOnly: boolean
  data: Record<string, unknown>
  onFieldChange: (
    fieldKey: string,
    updates: Partial<MarketplaceFieldMapping>
  ) => void
}

export interface MarketplaceTemplateTabConfig {
  label: string
  key: string
  product: string
  path: string
  render: () => React.ReactElement
}

export const createMarketplaceSalesforceTabs = (
  selectedCloud: string
): MarketplaceTemplateTabConfig[] => {
  return [
    {
      key: 'fields',
      label: 'Fields',
      product: 'marketplace',
      path: '',
      render: () => <MarketplaceTabFieldConfiguration />,
    },
    {
      key: 'dimensions',
      label: selectedCloud === CloudType.AZURE ? 'Plan' : 'Dimensions',
      product: 'marketplace',
      path: '',
      render: () => <MarketplaceTemplateDimensions />,
    },
    ...(selectedCloud === CloudType.AWS
      ? [
          {
            key: 'installments',
            label: 'Installments',
            product: 'marketplace',
            path: '',
            render: () => <MarketplaceInstallmentsTab />,
          },
        ]
      : []),
    {
      key: 'metadata',
      label: 'Metadata',
      product: 'marketplace',
      path: '',
      render: () => <MarketplaceTemplateMetadataTab />,
    },
    {
      key: 'recipientSource',
      label: 'Recipient source',
      product: 'marketplace',
      path: '',
      render: () => <MarketplaceRecipientSourceTab />,
    },
  ]
}

export type MarketplaceTemplateTableContainerSalesforceProps = {
  isViewOnly?: boolean
  data?: Record<string, unknown>
  onFieldChange?: MarketplaceTemplateTabRenderProps['onFieldChange']
  activeTabKey?: MarketplaceTemplateTabConfig['key']
  searchTerm?: string
  fieldFilter?: 'all' | 'mapped' | 'unmapped'
}

const defaultOnFieldChange: MarketplaceTemplateTabRenderProps['onFieldChange'] =
  () => {}

export const MarketplaceTemplateTableContainerSalesforce = React.memo(
  ({
    activeTabKey,
    searchTerm = '',
    fieldFilter = 'all',
  }: MarketplaceTemplateTableContainerSalesforceProps) => {
    const { classes } = useStyles()
    const [activeTabIndex, setActiveTabIndex] = useState(0)
    const selectedCloud = useCurrentCloudProvider()

    const mainTabs = useMemo(
      () =>
        createMarketplaceSalesforceTabs(selectedCloud).map(tab => ({
          ...tab,
          isError: false,
        })),
      [selectedCloud]
    )

    const handleTabChange = useCallback((index: number) => {
      setActiveTabIndex(index)
    }, [])

    if (activeTabKey) {
      return (
        <div
          className={classes.tabsRow}
          data-testid={`marketplace-salesforce-section-${activeTabKey}`}
        />
      )
    }

    return (
      <div className={classes.tabsRow}>
        <MainTabsV2
          tabs={mainTabs}
          activeTabIndex={activeTabIndex}
          onChange={handleTabChange}
          tabsType="main"
          variant="compactTabs"
        />
        {mainTabs[activeTabIndex]?.key === 'fields' ? (
          <MarketplaceTabFieldConfiguration
            searchTerm={searchTerm}
            fieldFilter={fieldFilter}
          />
        ) : (
          mainTabs[activeTabIndex]?.render()
        )}
      </div>
    )
  }
)
